import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import InputBox from "../widget/InputTextBox";
import { Snackbar, Alert } from "@mui/material";
import "../css/contact.css";

const emailServiceId =
  process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_ks6di19";
const emailTemplateId =
  process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_wx7dwko";
const emailPublicKey =
  process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "jAyYtX2Y1Irf0I169";

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showToast = (message, severity = "success") => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    emailjs.init({ publicKey: emailPublicKey });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setToast({ open: false, message: "", severity: "success" });
  };

  const validateForm = () => {
    const { name, email, message } = formData;

    if (!name.trim()) {
      showToast("Please enter your name", "error");
      return false;
    }

    if (!email.trim()) {
      showToast("Please enter your email address", "error");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showToast("Please enter a valid email address", "error");
      return false;
    }

    if (!message.trim()) {
      showToast("Please enter your message", "error");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      message: formData.message,
      to_name: "Makite Desta",
      reply_to: formData.email,
    };

    try {
      if (!emailServiceId || !emailTemplateId || !emailPublicKey) {
        throw new Error("EmailJS configuration missing");
      }

      await emailjs.send(emailServiceId, emailTemplateId, templateParams);
      showToast(
        "Message sent successfully! I’ll get back to you soon.",
        "success",
      );
      setFormData({ name: "", email: "", message: "" });
      setTimeout(handleCloseToast, 5000);
    } catch (error) {
      showToast(
        "Email service unavailable right now. Please send your message directly to makitedesta@gmail.com.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.section
      id="contact"
      className="contact-section"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="section-header">
        <span className="section-tag">Contact & Connect</span>
        <h2>Get in Touch</h2>
      </div>

      <div className="contact-grid">
        <motion.div
          className="contact-card bio-card"
          initial={{ x: -30, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h3>About Me</h3>
          <p>
            I design and deliver fintech platforms, ERP systems, and digital
            products built for reliability, security, and business growth. My
            work spans mobile banking, SaaS platforms, internal business
            systems, and DevOps-driven delivery for teams that need scalable
            execution.
          </p>
          <p>
            Based in Addis Ababa, Ethiopia, I help teams turn complex
            operational challenges into systems customers trust and teams can
            scale with.
          </p>
        </motion.div>

        <motion.div
          className="contact-card form-card"
          initial={{ x: 30, opacity: 0 }}
          whileInView={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <h3>Send a message</h3>
          <form onSubmit={handleSubmit} className="contact-form">
            <InputBox
              label="Your name"
              name="name"
              value={formData.name}
              onChange={handleChange}
            />
            <InputBox
              label="Your email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
            />
            <InputBox
              label="Your message"
              name="message"
              type="textarea"
              value={formData.message}
              onChange={handleChange}
            />

            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </motion.div>
      </div>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity={toast.severity}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </motion.section>
  );
};
